/**
 * @fileoverview useTransactions - Main transaction management hook.
 *
 * This is the primary hook for all transaction-related functionality. It composes
 * multiple smaller hooks to provide a complete transaction management interface.
 *
 * @description This hook follows the "composition pattern" where complex logic is
 * broken down into focused sub-hooks:
 * - useMonthlyData: Data fetching and caching
 * - useTransactionForm: Form state management
 * - useTransactionEdit: Edit mode state
 * - useTransactionCategories: Category utilities
 * - useTransactionCalculations: Totals and allocations
 * - useTransactionValidation: Form validation
 * - useTransactionOperations: CRUD operations
 *
 * Key features:
 * - Automatic monthly data loading based on selected month
 * - Category filtering (active vs ceased)
 * - User category separation (for "who paid" tracking)
 * - Transaction calculations (totals, spending by category)
 * - Form state management (add/edit modes)
 * - CRUD operations with optimistic updates
 *
 * @module hooks/useTransactions
 */

import { useMonthlyData } from './useMonthlyData';
import { useTransactionForm } from './useTransactionForm';
import { useTransactionEdit } from './useTransactionEdit';
import { useTransactionCategories } from './useTransactionCategories';
import { useTransactionCalculations } from './useTransactionCalculations';
import { useTransactionValidation } from './useTransactionValidation';
import { useTransactionOperations } from './useTransactionOperations';
import { createCategoryWrappers } from './helpers/categoryWrappers';

/**
 * Main hook for managing transaction state and operations.
 * Composes multiple sub-hooks to provide complete transaction functionality.
 *
 * @param {Date} selectedMonth - Currently selected month
 * @param {Object} budgetTemplate - Budget template with category allocations
 * @param {Array<Object>} dbCategories - All categories from database
 * @param {string} loggedInUserName - Name of the currently logged-in user
 * @param {boolean} singleBankMode - Whether budget is in single bank mode (optional, default false)
 * @param {number} currentUserId - Current user's ID for realtime filtering (optional)
 * @param {Object} categoryBalances - Category balances keyed by category name (optional)
 * @param {boolean} fillNegativeFirst - When true, fill negative category balances first (optional)
 * @param {boolean} allowOverTarget - When true, fill entire negative balance even if exceeding target (optional)
 * @returns {Object} Complete transaction interface with data, operations, and utilities
 *
 * @example
 * const transactionHook = useTransactions(
 *   monthNav.selectedMonth,
 *   budgetHook.budgetTemplate,
 *   categoryHook.categories,
 *   authUser.userName,
 *   budgetInfo?.singleBankMode,
 *   authUser.userKey,
 *   categoryBalances,
 *   budgetInfo?.fillNegativeFirst,
 *   budgetInfo?.allowOverTarget
 * );
 *
 * // Access transaction data
 * console.log(transactionHook.transactions);
 * console.log(transactionHook.total);
 *
 * // Add a transaction
 * transactionHook.handleAddTransaction(newTransactionData);
 */
export const useTransactions = (selectedMonth, budgetTemplate, dbCategories, loggedInUserName, singleBankMode = false, currentUserId = null, categoryBalances = {}, fillNegativeFirst = false, allowOverTarget = false) => {
    // Use monthly data hook for efficient loading
    // Pass currentUserId for realtime event filtering
    const monthlyData = useMonthlyData(selectedMonth, currentUserId);

    // Filter out ceased categories based on current date
    const currentDate = new Date();
    const activeCategoryObjects = dbCategories.filter(cat => {
        if (!cat.endDate) return true;
        const endDate = new Date(cat.endDate);
        return currentDate < endDate;
    });

    // Separate user categories from regular categories
    // userCategoryObjects contains both the userKey and categoryName for dropdown use
    const userCategoryObjects = activeCategoryObjects
        .filter(cat => cat.userKey !== null && cat.userKey !== undefined);

    // Array of category names for backward compatibility with category logic
    const userCategories = userCategoryObjects.map(cat => cat.categoryName);

    // Array of user objects with userKey and userName for dropdown selection
    const users = userCategoryObjects.map(cat => ({
        userKey: cat.userKey,
        userName: cat.categoryName
    }));

    const allCategoryNames = activeCategoryObjects.map(cat => cat.categoryName);
    const regularCategories = activeCategoryObjects
        .filter(cat => cat.userKey === null || cat.userKey === undefined)
        .map(cat => cat.categoryName);

    // Initialize all sub-hooks
    // Pass users array (with userKey and userName) for paidBy dropdown
    const formState = useTransactionForm(users, loggedInUserName);
    const editState = useTransactionEdit(userCategories, dbCategories, singleBankMode, users);
    const validationState = useTransactionValidation();

    const categoryUtils = useTransactionCategories(
        activeCategoryObjects,
        allCategoryNames,
        userCategories
    );

    const calculations = useTransactionCalculations(
        monthlyData,
        allCategoryNames,
        budgetTemplate,
        selectedMonth,
        categoryBalances,
        fillNegativeFirst,
        allowOverTarget
    );

    const operations = useTransactionOperations(
        monthlyData,
        userCategories,
        formState,
        editState,
        validationState,
        singleBankMode,
        users
    );

    // Create category wrapper functions
    const categoryWrappers = createCategoryWrappers(categoryUtils, formState, editState);

    // Return all the state and functions that components need
    return {
        // Core data (from monthly data system)
        transactions: monthlyData.activeTransactions,
        setTransactions: monthlyData.setActiveTransactions,
        filteredTransactions: calculations.getFilteredTransactions(),
        total: calculations.getTotal(),
        spendingByCategory: calculations.getSpendingByCategory(),
        monthlyAllocations: calculations.getMonthlyAllocations(),

        // Monthly data info
        isLoading: monthlyData.isLoading,
        availableMonths: monthlyData.availableMonths,
        hasDataForMonth: monthlyData.hasDataForMonth,

        // Categories and users
        categories: allCategoryNames,
        regularCategories,
        userCategories,
        users,

        // Form state (from useTransactionForm)
        transactionType: formState.transactionType,
        setTransactionType: formState.setTransactionType,
        amount: formState.amount,
        setAmount: formState.setAmount,
        description: formState.description,
        setDescription: formState.setDescription,
        date: formState.date,
        setDate: formState.setDate,
        transactionCategories: formState.transactionCategories,
        setTransactionCategories: formState.setTransactionCategories,
        currentUserKey: formState.currentUserKey,
        setCurrentUserKey: formState.setCurrentUserKey,
        paidDirectly: formState.paidDirectly,
        setPaidDirectly: formState.setPaidDirectly,
        useTemplateForIncome: formState.useTemplateForIncome,
        setUseTemplateForIncome: formState.setUseTemplateForIncome,

        // Adjustment state (from useTransactionForm)
        adjustmentFromCategory: formState.adjustmentFromCategory,
        setAdjustmentFromCategory: formState.setAdjustmentFromCategory,
        adjustmentToCategory: formState.adjustmentToCategory,
        setAdjustmentToCategory: formState.setAdjustmentToCategory,

        // Edit state (from useTransactionEdit)
        editingId: editState.editingId,
        editVersion: editState.editVersion,
        setEditVersion: editState.setEditVersion,
        editTransactionType: editState.editTransactionType,
        setEditTransactionType: editState.setEditTransactionType,
        editAmount: editState.editAmount,
        setEditAmount: editState.setEditAmount,
        editDescription: editState.editDescription,
        setEditDescription: editState.setEditDescription,
        editDate: editState.editDate,
        setEditDate: editState.setEditDate,
        editTransactionCategories: editState.editTransactionCategories,
        setEditTransactionCategories: editState.setEditTransactionCategories,
        editCurrentUserKey: editState.editCurrentUserKey,
        setEditCurrentUserKey: editState.setEditCurrentUserKey,
        editPaidDirectly: editState.editPaidDirectly,
        setEditPaidDirectly: editState.setEditPaidDirectly,
        editUseTemplateForIncome: editState.editUseTemplateForIncome,
        setEditUseTemplateForIncome: editState.setEditUseTemplateForIncome,
        editAdjustmentFromCategory: editState.editAdjustmentFromCategory,
        setEditAdjustmentFromCategory: editState.setEditAdjustmentFromCategory,
        editAdjustmentToCategory: editState.editAdjustmentToCategory,
        setEditAdjustmentToCategory: editState.setEditAdjustmentToCategory,

        // Error state (from useTransactionValidation)
        errors: validationState.errors,
        setErrors: validationState.setErrors,
        editErrors: validationState.editErrors,
        setEditErrors: validationState.setEditErrors,

        // Actions (from useTransactionOperations)
        addTransaction: operations.addTransaction,
        deleteTransaction: operations.deleteTransaction,
        saveEdit: operations.saveEdit,

        // Edit actions (from useTransactionEdit)
        startEdit: editState.startEdit,
        cancelEdit: editState.cancelEdit,

        // Form actions (from useTransactionForm)
        resetForm: formState.resetForm,
        handlePositiveNumberInput: formState.handlePositiveNumberInput,

        // Calculations (from useTransactionCalculations)
        allocateIncomeByTemplate: calculations.allocateIncomeByTemplate,

        // Category management (from categoryWrappers)
        ...categoryWrappers,

        // Error management (from useTransactionValidation)
        clearError: validationState.clearError,
        clearCategoryError: validationState.clearCategoryError,
        clearEditError: validationState.clearEditError,
        clearEditCategoryError: validationState.clearEditCategoryError
    };
};
